import { useAppSelector } from '../../../store/hooks'
import { Navigate, useNavigate } from 'react-router-dom'
import { TemplatesTabView } from './TemplatesTabView'
import { TemplateLookup } from '../../../models/TemplateLibrary'
import { useCreateNodeMutation, useUpdateNodeMutation } from '../../../store/ai/inputParametersHierarchy'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useTemplateSelection } from './useTemplateSelection'
import React, { useContext, useState } from 'react'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { Button } from '../../../components/Button/Button'
import { Spinner } from 'react-bootstrap'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getInContextTrackingDetails } from './tracking'

const TemplateSelectHeader = () => {
  const navigate = useNavigate()

  const handleBackButtonClicked = () => {
    navigate(-1)
  }

  return (
    <div className='d-flex align-items-center justify-content-between zocks-pl-2.0 zocks-gap-2 zocks-pr-2.0'>
      <div className='d-flex gap-3 align-items-center'>
        <Button variant='link' className='back-button' onClick={handleBackButtonClicked}>
          <i className='bi bi-chevron-left' />
        </Button>
      </div>
    </div>
  )
}

export const TemplateSelect = () => {
  const { tracked } = useTracking()
  const { portalRef } = useContext(HeaderContext)
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()
  const { templateTabContent, templateSelectionParams, hierarchyId } = useTemplateSelection()
  const [triggerCreateNode] = useCreateNodeMutation()
  const [triggerUpdateNode] = useUpdateNodeMutation()
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false)

  if (!templateTabContent) {
    return <Navigate to='/workspace/setup/templates' replace />
  }

  const _assignTemplate = async (templateLookup: TemplateLookup) => {
    if (!templateSelectionParams) {
      return
    }
    if (templateSelectionParams.nodeId) {
      await triggerUpdateNode({
        accountId,
        type: templateSelectionParams.type,
        typeId: templateSelectionParams.typeId,
        nodeId: templateSelectionParams.nodeId,
        node: {
          templateId: templateLookup.id,
        },
      })
    } else if (hierarchyId) {
      await triggerCreateNode({
        accountId,
        type: templateSelectionParams.type,
        typeId: templateSelectionParams.typeId,
        node: {
          hierarchyId,
          templateId: templateLookup.id,
        },
      })
    }
  }

  const assignTemplate = tracked(
    _assignTemplate,
    {
      eventName: TrackEvent.TEMPLATE_ASSIGNED,
    },
    {
      condition: !!templateSelectionParams,
      eventDetailsExtractor: (templateLookup) =>
        getInContextTrackingDetails(templateLookup.id, templateSelectionParams!.type, templateSelectionParams!.typeId, templateSelectionParams!.context),
    },
  )

  const onClick: (templateLookup: TemplateLookup) => void = async (templateLookup) => {
    if (!templateSelectionParams) {
      return
    }
    setIsTemplatesLoading(true)
    try {
      await assignTemplate(templateLookup)
      navigate(-1)
    } finally {
      setIsTemplatesLoading(false)
    }
  }

  return (
    <div>
      {portalRef && portalRef.current ? createPortal(<TemplateSelectHeader />, portalRef.current) : null}
      {!isTemplatesLoading ? (
        <TemplatesTabView templatesTab={templateTabContent} selectedTemplateId={templateSelectionParams?.templateId} onClick={onClick} />
      ) : (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
    </div>
  )
}
